import React, { useEffect } from 'react';

import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import FormRow from '../../Shared/UI/FormRow/components/FormRow';
import Input from '../../Shared/UI/Input/components/Input';
import Select from '../../Shared/UI/Select/components/Select';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

import { setCursorPositionInDOM } from '../../RichTextEditor';

import { useProfileState, useProfileActions } from './ProfileContext';
import ExperienceDescriptionRichTextEditor from './ExperienceDescriptionRichTextEditor';
import ExperienceDescriptionFormattingControls from './ExperienceDescriptionFormattingControls';

import { createUserExperience } from './restApi';

import editorStyles from './ExperienceDescriptionRichTextEditor.module.scss';

const NewUserExperienceForm = () => {
  const profileState = useProfileState();
  const { onResponse, onError, updateNewUserExperienceFormTitle, updateNewUserExperienceFormBiopharmaGroupName, updateNewUserExperienceFormStartMonth, updateNewUserExperienceFormStartYear, updateNewUserExperienceFormEndMonth, updateNewUserExperienceFormEndYear, setNewUserExperienceEditorState, closeNewUserExperienceModal } = useProfileActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

  const editor = profileState?.newUserExperienceEditor;
  const documentManager = editor?.documentManager;
  const editorState = documentManager ? { documentManager } : null;

  useEffect(() => {
    if (documentManager) {
      setCursorPositionInDOM({
        editableContentId: editorStyles.experienceEditorContainer,
        isibuteDocument: documentManager.getCurrentDocument(),
      });
    }
  }, [documentManager?.historyIndex]);

  const handleSubmit = () => {
    const newUserExperienceForm = {
      ...profileState?.newUserExperienceForm,
      documentComponents: documentManager ? documentManager.getCurrentDocument().asJSON().documentComponents : undefined,
    };

    createUserExperience({
      onResponse: (response) => {
        onResponse({
          ...response,
          newUserExperienceEditor: null,
        });
      },
      onError,
      body: {
        authenticityToken,
        newUserExperienceForm,
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Field label="Title" id="title">
        <Input
          type="text"
          value={profileState?.newUserExperienceForm?.title || ''}
          onChange={(e) => {
            updateNewUserExperienceFormTitle(e.target.value);
          }}
        />
      </Field>

      <Field label="Organization Name" id="biopharmaGroupName">
        <Input
          type="text"
          value={profileState?.newUserExperienceForm?.biopharmaGroupName || ''}
          onChange={(e) => {
            updateNewUserExperienceFormBiopharmaGroupName(e.target.value);
          }}
        />
      </Field>

      <FormRow>
        <Field label="Start Month" id="startMonth">
          <Select
            value={profileState?.newUserExperienceForm?.startMonth || ''}
            onChange={(e) => {
              updateNewUserExperienceFormStartMonth(e.target.value);
            }}
          >
            {(profileState?.userExperienceStartMonthOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>

        <Field label="Start Year" id="startYear">
          <Select
            value={profileState?.newUserExperienceForm?.startYear || ''}
            onChange={(e) => {
              updateNewUserExperienceFormStartYear(e.target.value);
            }}
          >
            {(profileState?.userExperienceStartYearOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <FormRow>
        <Field label="End Month" id="endMonth">
          <Select
            value={profileState?.newUserExperienceForm?.endMonth || ''}
            onChange={(e) => {
              updateNewUserExperienceFormEndMonth(e.target.value);
            }}
          >
            {(profileState?.userExperienceEndMonthOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>

        <Field label="End Year" id="endYear">
          <Select
            value={profileState?.newUserExperienceForm?.endYear || ''}
            onChange={(e) => {
              updateNewUserExperienceFormEndYear(e.target.value);
            }}
          >
            {(profileState?.userExperienceEndYearOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <Field label="Description" id="description">
        {editorState ? (
          <div>
            <ExperienceDescriptionFormattingControls
              contentEditable={true}
              setState={setNewUserExperienceEditorState}
              activeStyleTypes={editor?.activeStyleTypes}
              activeFormat={editor?.activeFormat}
              activeHref={editor?.activeHref}
              editableContentId={editorStyles.experienceEditorContainer}
            />
            <ExperienceDescriptionRichTextEditor
              state={editorState}
              setState={setNewUserExperienceEditorState}
              contentEditable={true}
            />
          </div>
        ) : null}
      </Field>

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => {
              closeNewUserExperienceModal();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            Save Your Experience
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default NewUserExperienceForm;
